import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { DownloadIcon, ChevronDownIcon } from 'lucide-react';
import { ComparisonType } from './ComparisonSelector';
import { AcceptanceFilters } from './AcceptanceFilters';
import { KeyMetricsSection } from './KeyMetricsSection';
import { BreakdownTable } from './BreakdownTable';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity } from './GranularitySelector';
import { MetricChart } from './MetricChart';
import { DownloadReportModal } from './DownloadReportModal';
interface DateRange {
  start: Date;
  end: Date;
}
interface AcceptanceTabProps {
  dateRange: DateRange;
  granularity: string;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
type KeyMetric = 'success-rate' | 'accepted-volume' | 'accepted-payments';
export function AcceptanceTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AcceptanceTabProps) {
  const {
    t
  } = useTranslation('analytics');
  const [selectedMetric, setSelectedMetric] = useState<KeyMetric>('success-rate');
  const [chartData, setChartData] = useState<any[]>([]);
  const [previousChartData, setPreviousChartData] = useState<any[]>([]);
  const [comparison, setComparison] = useState<ComparisonType>('previous_period');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  // Filter states
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [successRateMetric, setSuccessRateMetric] = useState<'payment-success-rate' | 'authorization-rate'>('payment-success-rate');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Generate mock data based on date range
  useEffect(() => {
    const data = generateChartData(dateRange, selectedMetric);
    setChartData(data.current);
    setPreviousChartData(data.previous);
  }, [dateRange, selectedMetric]);
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const formatYAxis = (value: any) => {
    if (selectedMetric === 'success-rate') {
      return `${value}%`;
    } else if (selectedMetric === 'accepted-volume') {
      if (value >= 1000000) {
        return `${getTenant().currency.code} ${(value / 1000000).toFixed(1)}Mn`;
      }
      if (value >= 1000) {
        return `${getTenant().currency.code} ${(value / 1000).toFixed(0)}K`;
      }
      return `${getTenant().currency.code} ${value}`;
    } else {
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
      return value.toString();
    }
  };
  // Get Y-axis domain based on metric type
  const getYAxisDomain = () => {
    if (selectedMetric === 'success-rate') {
      return [0, 100];
    }
    return undefined; // Auto-scale for other metrics
  };
  // Define metrics for KeyMetricsSection - title changes based on successRateMetric filter
  const getMetricTitle = () => {
    return successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate';
  };
  const getMetricTooltip = () => {
    return successRateMetric === 'payment-success-rate' ? "Percentage of payments approved by payment method issuers, out of all attempted payments. Includes blocked payments, and payments that didn't complete 3D Secure authentication." : "Percentage of payments approved by payment method issuers, out of all attempted payments. Excludes blocked payments, and payments that didn't complete 3D Secure authentication.";
  };
  const metrics: Array<{
    id: KeyMetric;
    title: string;
    value: string;
    change: string;
    tooltipText: string;
  }> = [{
    id: 'success-rate',
    title: successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate',
    value: '98.00%',
    change: '-0.97%',
    tooltipText: successRateMetric === 'payment-success-rate' ? "Percentage of payments approved by payment method issuers, out of all attempted payments. Includes blocked payments, and payments that didn't complete 3D Secure authentication." : "Percentage of payments approved by payment method issuers, out of all attempted payments. Excludes blocked payments, and payments that didn't complete 3D Secure authentication."
  }, {
    id: 'accepted-volume',
    title: 'Accepted volume',
    value: `${getTenant().currency.code} 14.2Mn`,
    change: '+12.45%',
    tooltipText: 'Total amount of accepted payments. Defaults to your settlement currency.'
  }, {
    id: 'accepted-payments',
    title: 'Accepted payments',
    value: '48,500',
    change: '+9.82%',
    tooltipText: 'Total number of payments approved by payment method issuers.'
  }];
  // Get data key based on selected metric
  const getDataKey = () => {
    if (selectedMetric === 'success-rate') return 'rate';
    if (selectedMetric === 'accepted-volume') return 'amount';
    return 'count';
  };
  // Breakdown table data
  const breakdownRows = [{
    category: 'Accepted payments',
    volume: `${getTenant().currency.code} 14,200,000`,
    share: '98.04%',
    change: '+12.45%'
  }, {
    category: '✓ Accepted',
    volume: `${getTenant().currency.code} 14,200,000`,
    share: '98.04%',
    change: '+12.45%',
    isSubItem: true
  }, {
    category: 'Failed payments',
    volume: `${getTenant().currency.code} 285,000`,
    share: '1.96%',
    change: '+5.21%'
  }, {
    category: 'Issuer declined',
    volume: `${getTenant().currency.code} 236,300`,
    share: '1.63%',
    change: '+4.85%',
    isSubItem: true
  }, {
    category: '3DS failures',
    volume: `${getTenant().currency.code} 34,200`,
    share: '0.24%',
    change: '+8.12%',
    isSubItem: true
  }, {
    category: 'Blocked',
    volume: `${getTenant().currency.code} 14,500`,
    share: '0.10%',
    change: '-2.30%',
    isSubItem: true
  }, {
    category: 'Total',
    volume: `${getTenant().currency.code} 14,485,000`,
    share: '100.00%',
    change: '',
    isTotal: true
  }];
  return <div className="space-y-6" data-id="element-727">
      {/* Main Filters Row - Larger buttons */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-728">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-729" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-730" />

        <AcceptanceFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} successRateMetric={successRateMetric} onSuccessRateMetricChange={setSuccessRateMetric} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-731" />
      </div>

      {/* Key Metrics Section with Chart */}
      <KeyMetricsSection title={t('components.AcceptanceTab.attrs.key_metrics')} metrics={metrics} selectedMetricId={selectedMetric} onMetricSelect={id => setSelectedMetric(id as KeyMetric)} chartData={chartData} previousChartData={previousChartData} dataKey={getDataKey()} yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatYAxis(value)} dateFormatter={formatDate} updateTime="Updated yesterday" chartColor="#D4AF37" comparison={comparison} onComparisonChange={setComparison} dateRange={dateRange} yAxisDomain={getYAxisDomain()} onDownload={() => setShowDownloadModal(true)} data-id="element-732" />

      {/* Breakdown Table - only show for accepted-volume */}
      {selectedMetric === 'accepted-volume' && <BreakdownTable rows={breakdownRows} data-id="element-733" />}

      {/* Card Payments Breakdown */}
      <CardPaymentsBreakdown dateRange={dateRange} onDownload={() => setShowDownloadModal(true)} successRateMetric={successRateMetric} data-id="element-734" />

      {/* Failed Card Payments */}
      <FailedCardPayments dateRange={dateRange} onDownload={() => setShowDownloadModal(true)} data-id="element-735" />

      {/* Blocked Card Payments */}
      <BlockedCardPayments data-id="element-736" />

      {/* Download Modal */}
      <DownloadReportModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} dateRange={dateRange} deduplication={deduplication} data-id="element-737" />
    </div>;
}
// Card Payments Breakdown Component
type BreakdownView = 'payment-volume' | 'payments' | 'share-of-payments' | 'authorization-rate';
type PivotOption = '3DS outcome' | 'Card brand' | 'Card country' | 'Card type' | 'Currency' | 'CVC response' | 'Input method' | 'Interaction type' | 'Network token status' | 'Postal code response' | 'Retry status';
const COLOR_PALETTE = ['#4F46E5', '#6B7280', '#059669', '#D97706', '#94A3B8', '#DC2626', '#7C3AED'];
const PIVOT_CONFIG: Record<PivotOption, {
  key: string;
  label: string;
  volumeShare: number;
  successRateBase: number;
}[]> = {
  'Card type': [{
    key: 'creditCard',
    label: 'Credit card',
    volumeShare: 0.88,
    successRateBase: 97.73
  }, {
    key: 'debitCard',
    label: 'Debit card',
    volumeShare: 0.12,
    successRateBase: 100.0
  }],
  'Card brand': [{
    key: 'visa',
    label: 'Visa',
    volumeShare: 0.48,
    successRateBase: 98.2
  }, {
    key: 'mastercard',
    label: 'Mastercard',
    volumeShare: 0.32,
    successRateBase: 97.5
  }, {
    key: 'amex',
    label: 'American Express',
    volumeShare: 0.15,
    successRateBase: 99.1
  }, {
    key: 'jcb',
    label: 'JCB',
    volumeShare: 0.03,
    successRateBase: 96.8
  }, {
    key: 'unionpay',
    label: 'UnionPay',
    volumeShare: 0.02,
    successRateBase: 95.5
  }],
  'Card country': [{
    key: 'my',
    label: 'Maldives',
    volumeShare: 0.55,
    successRateBase: 98.5
  }, {
    key: 'us',
    label: 'United States',
    volumeShare: 0.25,
    successRateBase: 97.2
  }, {
    key: 'uk',
    label: 'United Kingdom',
    volumeShare: 0.1,
    successRateBase: 98.1
  }, {
    key: 'sg',
    label: 'Singapore',
    volumeShare: 0.05,
    successRateBase: 99.0
  }, {
    key: 'others',
    label: 'Others',
    volumeShare: 0.05,
    successRateBase: 96.5
  }],
  '3DS outcome': [{
    key: 'authenticated',
    label: 'Authenticated',
    volumeShare: 0.65,
    successRateBase: 99.5
  }, {
    key: 'notAuthenticated',
    label: 'Not authenticated',
    volumeShare: 0.2,
    successRateBase: 95.0
  }, {
    key: 'attempted',
    label: 'Attempted',
    volumeShare: 0.1,
    successRateBase: 97.0
  }, {
    key: 'failed',
    label: 'Failed',
    volumeShare: 0.05,
    successRateBase: 85.0
  }],
  Currency: [{
    key: 'tenant',
    label: getTenant().currency.code,
    volumeShare: 0.6,
    successRateBase: 98.5
  }, {
    key: 'usd-2',
    label: getTenant().currency.code,
    volumeShare: 0.25,
    successRateBase: 97.5
  }, {
    key: 'gbp',
    label: 'GBP',
    volumeShare: 0.08,
    successRateBase: 98.0
  }, {
    key: 'sgd',
    label: 'SGD',
    volumeShare: 0.04,
    successRateBase: 99.0
  }, {
    key: 'others',
    label: 'Others',
    volumeShare: 0.03,
    successRateBase: 96.0
  }],
  'CVC response': [{
    key: 'match',
    label: 'Match',
    volumeShare: 0.85,
    successRateBase: 99.0
  }, {
    key: 'noMatch',
    label: 'No match',
    volumeShare: 0.05,
    successRateBase: 80.0
  }, {
    key: 'notProcessed',
    label: 'Not processed',
    volumeShare: 0.08,
    successRateBase: 95.0
  }, {
    key: 'notProvided',
    label: 'Not provided',
    volumeShare: 0.02,
    successRateBase: 90.0
  }],
  'Input method': [{
    key: 'ecommerce',
    label: 'E-commerce',
    volumeShare: 0.7,
    successRateBase: 97.5
  }, {
    key: 'contactless',
    label: 'Contactless',
    volumeShare: 0.2,
    successRateBase: 99.5
  }, {
    key: 'chip',
    label: 'Chip',
    volumeShare: 0.08,
    successRateBase: 99.8
  }, {
    key: 'manual',
    label: 'Manual entry',
    volumeShare: 0.02,
    successRateBase: 92.0
  }],
  'Interaction type': [{
    key: 'customerInitiated',
    label: 'Customer-initiated',
    volumeShare: 0.8,
    successRateBase: 97.5
  }, {
    key: 'merchantInitiated',
    label: 'Merchant-initiated',
    volumeShare: 0.2,
    successRateBase: 99.0
  }],
  'Network token status': [{
    key: 'tokenized',
    label: 'Tokenized',
    volumeShare: 0.4,
    successRateBase: 99.5
  }, {
    key: 'notTokenized',
    label: 'Not tokenized',
    volumeShare: 0.6,
    successRateBase: 97.0
  }],
  'Postal code response': [{
    key: 'match',
    label: 'Match',
    volumeShare: 0.75,
    successRateBase: 99.0
  }, {
    key: 'noMatch',
    label: 'No match',
    volumeShare: 0.05,
    successRateBase: 85.0
  }, {
    key: 'notProvided',
    label: 'Not provided',
    volumeShare: 0.2,
    successRateBase: 97.5
  }],
  'Retry status': [{
    key: 'firstAttempt',
    label: 'First attempt',
    volumeShare: 0.95,
    successRateBase: 98.5
  }, {
    key: 'retry',
    label: 'Retry',
    volumeShare: 0.05,
    successRateBase: 88.0
  }]
};
function CardPaymentsBreakdown({
  dateRange,
  onDownload,
  successRateMetric
}: {
  dateRange: DateRange;
  onDownload: () => void;
  successRateMetric: 'payment-success-rate' | 'authorization-rate';
}) {
  const {
    t
  } = useTranslation('analytics');
  const [selectedView, setSelectedView] = useState<BreakdownView>('payment-volume');
  const [pivotBy, setPivotBy] = useState<PivotOption>('Card type');
  const [showPivotDropdown, setShowPivotDropdown] = useState(false);
  const pivotRef = useRef<HTMLDivElement>(null);
  const pivotOptions: PivotOption[] = ['3DS outcome', 'Card brand', 'Card country', 'Card type', 'Currency', 'CVC response', 'Input method', 'Interaction type', 'Network token status', 'Postal code response', 'Retry status'];
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pivotRef.current && !pivotRef.current.contains(event.target as Node)) {
        setShowPivotDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const pivotCategories = PIVOT_CONFIG[pivotBy];
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    pivotCategories.forEach((cat, i) => {
      map[cat.key] = COLOR_PALETTE[i % COLOR_PALETTE.length];
    });
    return map;
  }, [pivotBy, pivotCategories]);
  const chartData = generateCardBreakdownData(dateRange, selectedView, pivotBy);
  const getChartTitle = () => {
    switch (selectedView) {
      case 'payment-volume':
        return 'Payment volume';
      case 'payments':
        return 'Payments';
      case 'share-of-payments':
        return 'Share of payments';
      case 'authorization-rate':
        return 'Authorization rate';
    }
  };
  const getChartValue = () => {
    switch (selectedView) {
      case 'payment-volume':
        return `${getTenant().currency.code} 3,260.80`;
      case 'payments':
        return '50';
      case 'share-of-payments':
        return '100.00%';
      case 'authorization-rate':
        return '98.00%';
    }
  };
  const formatYAxis = (value: any) => {
    switch (selectedView) {
      case 'payment-volume':
        // Add decimal for better readability when values are small
        if (value >= 1000) {
          return `${getTenant().currency.code} ${(value / 1000).toFixed(1)}K`;
        }
        return `${getTenant().currency.code} ${value}`;
      case 'payments':
        return value.toString();
      case 'share-of-payments':
        return `${value}%`;
      case 'authorization-rate':
        return `${value}%`;
    }
  };
  const getYAxisDomain = () => {
    if (selectedView === 'share-of-payments' || selectedView === 'authorization-rate') {
      return [0, 100];
    }
    return undefined;
  };
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Custom tooltip formatter for proper labels
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3" data-id="element-738">
          <p className="text-sm font-medium text-gray-900 mb-2" data-id="element-739">{label}</p>
          {payload.map((entry: any, index: number) => {
          const category = pivotCategories.find(c => c.key === entry.dataKey);
          const displayLabel = category ? category.label : entry.dataKey;
          const formattedValue = selectedView === 'payment-volume' ? formatCurrencyAmount(entry.value, {
            fractionDigits: 2
          }) : selectedView === 'share-of-payments' || selectedView === 'authorization-rate' ? `${entry.value.toFixed(2)}%` : entry.value;
          return <div key={index} className="flex items-center justify-between gap-4 mb-1" data-id="element-740">
                <div className="flex items-center gap-2" data-id="element-741">
                  <div className="w-2 h-2 rounded-full" style={{
                backgroundColor: entry.color
              }} data-id="element-742" />

                  <span className="text-sm text-gray-700" data-id="element-743">{displayLabel}</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-744">
                  {formattedValue}
                </span>
              </div>;
        })}
        </div>;
    }
    return null;
  };
  // Generate dynamic table rows based on pivot config
  const tableRows = useMemo(() => {
    const totalVolume = 3260.8;
    const totalPayments = 50;
    return [...pivotCategories.map(cat => {
      const volume = totalVolume * cat.volumeShare;
      const payments = Math.round(totalPayments * cat.volumeShare);
      return {
        category: cat.label,
        key: cat.key,
        volume: formatCurrencyAmount(volume, {
          fractionDigits: 2
        }),
        payments: payments.toString(),
        share: `${(cat.volumeShare * 100).toFixed(2)}%`,
        rate: `${cat.successRateBase.toFixed(2)}%`
      };
    }), {
      category: 'Total',
      key: 'total',
      volume: formatCurrencyAmount(totalVolume, {
        fractionDigits: 2
      }),
      payments: totalPayments.toString(),
      share: '100.00%',
      rate: '98.00%',
      isTotal: true
    }];
  }, [pivotCategories]);
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-745">
      <div className="flex items-center justify-between mb-4" data-id="element-746">
        <div data-id="element-747">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-748">
            {t('components.CardPaymentsBreakdown.card_payments_breakdown')}
          </h3>
          <p className="text-sm text-gray-600 mt-1" data-id="element-749">
            {t('components.CardPaymentsBreakdown.analyze_card_authorization_success_and_the')}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-750">
          <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-751">
            <DownloadIcon size={14} data-id="element-752" />
            {t('components.CardPaymentsBreakdown.download')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6" data-id="element-753">
        <div className="flex items-center gap-2" data-id="element-754">
          <span className="text-sm text-gray-700" data-id="element-755">
            {t('components.CardPaymentsBreakdown.pivot_by')}
          </span>
          <div className="relative" ref={pivotRef} data-id="element-756">
            <button onClick={() => setShowPivotDropdown(!showPivotDropdown)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-757">
              <span data-id="element-758">{pivotBy}</span>
              <ChevronDownIcon size={16} data-id="element-759" />
            </button>

            {showPivotDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-760">
                {pivotOptions.map(option => <button key={option} onClick={() => {
              setPivotBy(option);
              setShowPivotDropdown(false);
            }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${pivotBy === option ? 'text-gray-900 font-medium' : 'text-gray-700'}`} data-id="element-761">
                    <span data-id="element-762">{option}</span>
                    {pivotBy === option && <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-blue-600" data-id="element-763">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-764" />
                      </svg>}
                  </button>)}
              </div>}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-765">
          <button onClick={() => setSelectedView('payment-volume')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'payment-volume' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-766">
            {t('components.CardPaymentsBreakdown.payment_volume')}
          </button>
          <button onClick={() => setSelectedView('payments')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'payments' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-767">
            {t('components.CardPaymentsBreakdown.payments')}
          </button>
          <button onClick={() => setSelectedView('share-of-payments')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'share-of-payments' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-768">
            {t('components.CardPaymentsBreakdown.share_of_payments')}
          </button>
          <button onClick={() => setSelectedView('authorization-rate')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'authorization-rate' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-769">
            {successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate'}
          </button>
        </div>
      </div>

      {/* Chart section */}
      {(() => {
      if (selectedView === 'authorization-rate') {
        return <>
              <div className="mb-4" data-id="element-770">
                <div className="text-sm text-gray-600 mb-1 flex items-center gap-1" data-id="element-771">
                  {successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate'}
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400" data-id="element-772">
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-773" />

                    <path d="M7 4.5V7M7 9.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-774" />
                  </svg>
                </div>
                <div className="text-2xl font-semibold text-gray-900" data-id="element-775">
                  {getChartValue()}
                </div>
              </div>

              <div className="h-64 mb-6" role="img" aria-label="Acceptance rate trend chart" data-id="element-776">
                <ResponsiveContainer width="100%" height="100%" data-id="element-777">
                  <LineChart data={chartData.current} margin={{
                left: 0,
                right: 0,
                top: 5,
                bottom: 20
              }} data-id="element-778">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" data-id="element-779" />

                    <XAxis dataKey="date" tick={{
                  fill: '#666',
                  fontSize: 11
                }} data-id="element-780" />

                    <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
                  fill: '#666',
                  fontSize: 12
                }} domain={[0, 100]} data-id="element-781" />

                    <Tooltip content={<CustomTooltip data-id="element-783" />} data-id="element-782" />
                    {pivotCategories.map(cat => <Line key={cat.key} type="monotone" dataKey={cat.key} name={cat.label} stroke={colorMap[cat.key]} strokeWidth={2} dot={false} activeDot={{
                  r: 4,
                  fill: colorMap[cat.key],
                  stroke: '#fff',
                  strokeWidth: 2
                }} data-id="element-784" />)}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center justify-center gap-6 mt-6 flex-wrap" data-id="element-785">
                {pivotCategories.map(cat => <div key={cat.key} className="flex items-center gap-2" data-id="element-786">
                    <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: colorMap[cat.key]
              }} data-id="element-787" />

                    <span className="text-xs text-gray-600" data-id="element-788">{cat.label}</span>
                  </div>)}
              </div>

              <div className="text-xs text-gray-500 mb-4 mt-6" data-id="element-789">
                {t('components.CardPaymentsBreakdown.updated_yesterday')}
              </div>
            </>;
      }
      return <>
            <div className="mb-4" data-id="element-790">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-1" data-id="element-791">
                {getChartTitle()}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400" data-id="element-792">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-793" />

                  <path d="M7 4.5V7M7 9.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-794" />
                </svg>
              </div>
              <div className="text-2xl font-semibold text-gray-900" data-id="element-795">
                {getChartValue()}
              </div>
            </div>

            {/* Chart - Different types based on selected view */}
            <div className="h-64 mb-6" role="img" aria-label="Acceptance bar chart" data-id="element-796">
              <ResponsiveContainer width="100%" height="100%" data-id="element-797">
                <BarChart data={chartData.current} margin={{
              left: 0,
              right: 0,
              top: 5,
              bottom: 20
            }} data-id="element-798">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-799" />

                  <XAxis dataKey="date" tick={{
                fill: '#666',
                fontSize: 11
              }} data-id="element-800" />

                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
                fill: '#666',
                fontSize: 12
              }} domain={getYAxisDomain()} data-id="element-801" />

                  <Tooltip content={<CustomTooltip data-id="element-803" />} data-id="element-802" />
                  {pivotCategories.map((cat, i) => <Bar key={cat.key} dataKey={cat.key} stackId="a" fill={colorMap[cat.key]} radius={i === pivotCategories.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]} data-id="element-804" />)}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-center gap-6 mt-6 flex-wrap" data-id="element-805">
              {pivotCategories.map(cat => <div key={cat.key} className="flex items-center gap-2" data-id="element-806">
                  <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: colorMap[cat.key]
            }} data-id="element-807" />

                  <span className="text-xs text-gray-600" data-id="element-808">{cat.label}</span>
                </div>)}
            </div>

            <div className="text-xs text-gray-500 mb-4 mt-6" data-id="element-809">
              {t('components.CardPaymentsBreakdown.updated_yesterday')}
            </div>
          </>;
    })()}

      {/* Table */}
      <table className="w-full" data-id="element-810">
        <thead data-id="element-811">
          <tr className="border-b border-gray-200" data-id="element-812">
            <th className="text-left text-sm font-medium text-gray-700 pb-3" data-id="element-813">
              {pivotBy}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-814">
              {t('components.CardPaymentsBreakdown.payment_volume')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-815">
              {t('components.CardPaymentsBreakdown.payments')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-816">
              {t('components.CardPaymentsBreakdown.share_of_payments')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-817">
              {t('components.CardPaymentsBreakdown.success_rate')}
            </th>
          </tr>
        </thead>
        <tbody data-id="element-818">
          {tableRows.map((row, index) => <tr key={index} className={row.isTotal ? 'border-t-2 border-gray-300' : 'border-b border-gray-200'} data-id="element-819">
              <td className={`py-3 text-sm flex items-center gap-2 ${row.isTotal ? 'font-semibold text-gray-900' : 'text-gray-900'}`} data-id="element-820">
                {!row.isTotal && <div className="relative w-4 h-4 flex items-center justify-center rounded cursor-pointer" style={{
              backgroundColor: colorMap[row.key]
            }} data-id="element-821">
                    <input type="checkbox" className="absolute inset-0 opacity-0 cursor-pointer" checked readOnly data-id="element-822" />

                    <svg className="w-3 h-3 text-white pointer-events-none" viewBox="0 0 12 12" fill="none" data-id="element-823">
                      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-824" />
                    </svg>
                  </div>}
                {row.category}
              </td>
              <td className={`py-3 text-sm text-right ${row.isTotal ? 'font-semibold text-gray-900' : 'text-gray-900'}`} data-id="element-825">
                {row.volume}
              </td>
              <td className={`py-3 text-sm text-right ${row.isTotal ? 'font-semibold text-gray-900' : 'text-gray-900'}`} data-id="element-826">
                {row.payments}
              </td>
              <td className={`py-3 text-sm text-right ${row.isTotal ? 'font-semibold text-gray-900' : 'text-gray-900'}`} data-id="element-827">
                {row.share}
              </td>
              <td className={`py-3 text-sm text-right ${row.isTotal ? 'font-semibold text-gray-900' : 'text-gray-900'}`} data-id="element-828">
                {row.rate}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
// Failed Card Payments Component
type FailedView = 'failed-volume' | 'failed' | 'share-of-failures' | 'failure-rate';
function FailedCardPayments({
  dateRange,
  onDownload
}: {
  dateRange: DateRange;
  onDownload: () => void;
}) {
  const {
    t
  } = useTranslation('analytics');
  const [selectedView, setSelectedView] = useState<FailedView>('failed-volume');
  const chartData = generateFailedPaymentsData(dateRange, selectedView);
  const getChartTitle = () => {
    switch (selectedView) {
      case 'failed-volume':
        return 'Failed payment volume';
      case 'failed':
        return 'Failed';
      case 'share-of-failures':
        return 'Share of failures';
      case 'failure-rate':
        return 'Failure rate';
    }
  };
  const getChartValue = () => {
    switch (selectedView) {
      case 'failed-volume':
        return `${getTenant().currency.code} 285K`;
      case 'failed':
        return '980';
      case 'share-of-failures':
        return '100.00%';
      case 'failure-rate':
        return '1.96%';
    }
  };
  const formatYAxis = (value: any) => {
    switch (selectedView) {
      case 'failed-volume':
        if (value >= 1000000) {
          return `${getTenant().currency.code} ${(value / 1000000).toFixed(1)}Mn`;
        }
        if (value >= 1000) {
          return `${getTenant().currency.code} ${(value / 1000).toFixed(0)}K`;
        }
        return `${getTenant().currency.code} ${value}`;
      case 'failed':
        return value.toString();
      case 'share-of-failures':
        return `${value}%`;
      case 'failure-rate':
        return `${value}%`;
    }
  };
  const getYAxisDomain = () => {
    if (selectedView === 'share-of-failures' || selectedView === 'failure-rate') {
      return [0, 100];
    }
    return undefined;
  };
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Custom tooltip formatter for proper labels
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3" data-id="element-829">
          <p className="text-sm font-medium text-gray-900 mb-2" data-id="element-830">{label}</p>
          {payload.map((entry: any, index: number) => {
          const labelMap: {
            [key: string]: string;
          } = {
            insufficientFunds: 'Insufficient funds',
            incorrectCardDetails: 'Incorrect card details',
            issuerDeclined: 'Issuer declined / do not honor',
            suspectedFraud: 'Suspected fraud',
            expiredCard: 'Expired card',
            other: 'Other'
          };
          const displayLabel = labelMap[entry.dataKey] || entry.dataKey;
          const formattedValue = selectedView === 'failed-volume' ? formatCurrencyAmount(entry.value, {
            fractionDigits: 2
          }) : entry.value;
          return <p key={index} className="text-sm text-gray-700" data-id="element-831">
                {displayLabel} : {formattedValue}
              </p>;
        })}
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-832">
      <div className="flex items-center justify-between mb-4" data-id="element-833">
        <div data-id="element-834">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-835">
            {t('components.FailedCardPayments.failed_card_payments')}
          </h3>
          <p className="text-sm text-gray-600 mt-1" data-id="element-836">
            {t('components.FailedCardPayments.find_out_why_card_payments_failed')}{' '}
            <a href="#" className="text-blue-600 hover:underline" data-id="element-837">
              {t('components.FailedCardPayments.learn_more_about_decline_codes')}
            </a>
            .
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-838">
          <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-839">
            <DownloadIcon size={14} data-id="element-840" />
            {t('components.CardPaymentsBreakdown.download')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6" data-id="element-841">
        <button onClick={() => setSelectedView('failed-volume')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failed-volume' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-842">
          {t('components.FailedCardPayments.failed_volume')}
        </button>
        <button onClick={() => setSelectedView('failed')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failed' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-843">
          {t('components.FailedCardPayments.failed')}
        </button>
        <button onClick={() => setSelectedView('share-of-failures')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'share-of-failures' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-844">
          {t('components.FailedCardPayments.share_of_failures')}
        </button>
        <button onClick={() => setSelectedView('failure-rate')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failure-rate' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-845">
          {t('components.FailedCardPayments.failure_rate')}
        </button>
      </div>

      {/* Use MetricChart for Failure rate, custom charts for others */}
      {selectedView === 'failure-rate' ? <MetricChart title={getChartTitle()} currentValue={getChartValue()} growthPercent={0} previousValue="2.00%" data={chartData.current} previousData={chartData.previous} dataKey="value" yAxisFormatter={formatYAxis} tooltipValueFormatter={formatYAxis} dateFormatter={formatDate} updateTime="Updated yesterday" chartColor="#D4AF37" data-id="element-846" /> : <>
          <div className="mb-4" data-id="element-847">
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-1" data-id="element-848">
              {getChartTitle()}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400" data-id="element-849">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-850" />

                <path d="M7 4.5V7M7 9.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-851" />
              </svg>
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-852">
              {getChartValue()}
            </div>
          </div>

          {/* Chart - Different types based on selected view */}
          <div className="h-64 mb-6" role="img" aria-label="Failure breakdown chart" data-id="element-853">
            <ResponsiveContainer width="100%" height="100%" data-id="element-854">
              {selectedView === 'share-of-failures' ? <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-855">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-856" />

                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-857" />

                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-858" />

                  <Tooltip content={<CustomTooltip data-id="element-860" />} data-id="element-859" />
                  <Bar dataKey="insufficientFunds" stackId="a" fill="#4F46E5" data-id="element-861" />
                  <Bar dataKey="incorrectCardDetails" stackId="a" fill="#059669" data-id="element-862" />

                  <Bar dataKey="issuerDeclined" stackId="a" fill="#D97706" data-id="element-863" />
                  <Bar dataKey="suspectedFraud" stackId="a" fill="#DC2626" data-id="element-864" />
                  <Bar dataKey="expiredCard" stackId="a" fill="#7C3AED" data-id="element-865" />
                  <Bar dataKey="other" stackId="a" fill="#94A3B8" radius={[2, 2, 0, 0]} data-id="element-866" />
                </BarChart> : <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-867">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-868" />

                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-869" />

                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-870" />

                  <Tooltip content={<CustomTooltip data-id="element-872" />} data-id="element-871" />
                  <Bar dataKey="insufficientFunds" stackId="a" fill="#4F46E5" data-id="element-873" />
                  <Bar dataKey="incorrectCardDetails" stackId="a" fill="#059669" data-id="element-874" />

                  <Bar dataKey="issuerDeclined" stackId="a" fill="#D97706" data-id="element-875" />
                  <Bar dataKey="suspectedFraud" stackId="a" fill="#DC2626" data-id="element-876" />
                  <Bar dataKey="expiredCard" stackId="a" fill="#7C3AED" data-id="element-877" />
                  <Bar dataKey="other" stackId="a" fill="#94A3B8" radius={[2, 2, 0, 0]} data-id="element-878" />
                </BarChart>}
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-4 mt-4 mb-2 flex-wrap" data-id="element-879">
            {[{
          label: 'Insufficient funds',
          color: '#4F46E5'
        }, {
          label: 'Incorrect card details',
          color: '#059669'
        }, {
          label: 'Issuer declined / do not honor',
          color: '#D97706'
        }, {
          label: 'Suspected fraud',
          color: '#DC2626'
        }, {
          label: 'Expired card',
          color: '#7C3AED'
        }, {
          label: 'Other',
          color: '#94A3B8'
        }].map(item => <div key={item.label} className="flex items-center gap-1.5" data-id="element-880">
                <div className="w-2.5 h-2.5 rounded-full" style={{
            backgroundColor: item.color
          }} data-id="element-881" />

                <span className="text-xs text-gray-600" data-id="element-882">{item.label}</span>
              </div>)}
          </div>

          <div className="text-xs text-gray-500 mb-4" data-id="element-883">
            {t('components.CardPaymentsBreakdown.updated_yesterday')}
          </div>
        </>}

      {/* Table */}
      <table className="w-full" data-id="element-884">
        <thead data-id="element-885">
          <tr className="border-b border-gray-200" data-id="element-886">
            <th className="text-left text-sm font-medium text-gray-700 pb-3" data-id="element-887">
              {t('components.FailedCardPayments.reason')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-888">
              {t('components.FailedCardPayments.failed_volume')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-889">
              {t('components.FailedCardPayments.failed_count')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-890">
              {t('components.FailedCardPayments.share_of_failures')}
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-891">
              {t('components.FailedCardPayments.failure_rate')}
            </th>
          </tr>
        </thead>
        <tbody data-id="element-892">
          {[{
          reason: 'Insufficient funds',
          color: '#4F46E5',
          volume: `${getTenant().currency.code} 99.8K`,
          count: '343',
          share: '35.01%',
          rate: '0.69%'
        }, {
          reason: 'Incorrect card details',
          color: '#059669',
          volume: `${getTenant().currency.code} 57.0K`,
          count: '196',
          share: '20.00%',
          rate: '0.39%'
        }, {
          reason: 'Issuer declined / do not honor',
          color: '#D97706',
          volume: `${getTenant().currency.code} 51.3K`,
          count: '176',
          share: '17.96%',
          rate: '0.35%'
        }, {
          reason: 'Suspected fraud',
          color: '#DC2626',
          volume: `${getTenant().currency.code} 34.2K`,
          count: '118',
          share: '12.04%',
          rate: '0.24%'
        }, {
          reason: 'Expired card',
          color: '#7C3AED',
          volume: `${getTenant().currency.code} 28.5K`,
          count: '98',
          share: '10.00%',
          rate: '0.20%'
        }, {
          reason: 'Other',
          color: '#94A3B8',
          volume: `${getTenant().currency.code} 14.2K`,
          count: '49',
          share: '5.00%',
          rate: '0.10%'
        }].map((row, i) => <tr key={i} className="border-b border-gray-200" data-id="element-893">
              <td className="py-3 text-sm text-gray-900 flex items-center gap-2" data-id="element-894">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{
              backgroundColor: row.color
            }} data-id="element-895" />

                {row.reason}
              </td>
              <td className="py-3 text-sm text-right text-gray-900" data-id="element-896">
                {row.volume}
              </td>
              <td className="py-3 text-sm text-right text-gray-900" data-id="element-897">
                {row.count}
              </td>
              <td className="py-3 text-sm text-right text-gray-900" data-id="element-898">
                {row.share}
              </td>
              <td className="py-3 text-sm text-right text-gray-900" data-id="element-899">
                {row.rate}
              </td>
            </tr>)}
          <tr className="border-t-2 border-gray-300" data-id="element-900">
            <td className="py-3 text-sm font-semibold text-gray-900" data-id="element-901">
              {t('components.FailedCardPayments.total')}
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-902">
              {t('components.FailedCardPayments.usd_285k')}
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-903">
              980
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-904">
              100.00%
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-905">
              1.96%
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
}
// Blocked Card Payments Component
function BlockedCardPayments() {
  const {
    t
  } = useTranslation('analytics');
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-906">
      <div className="flex items-center justify-between mb-4" data-id="element-907">
        <div data-id="element-908">
          <h3 className="text-sm font-medium text-gray-900" data-id="element-909">
            {t('components.BlockedCardPayments.blocked_card_payments')}
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-910">
            {t('components.BlockedCardPayments.find_out_why_some_card_payments')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center py-12" data-id="element-911">
        <div className="text-center" data-id="element-912">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="element-913">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-914">
              <circle cx="32" cy="32" r="30" stroke="#e5e7eb" strokeWidth="2" data-id="element-915" />
              <path d="M32 20v16M32 44h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" data-id="element-916" />
            </svg>
          </div>
          <p className="text-sm text-gray-600" data-id="element-917">
            {t('components.BlockedCardPayments.no_payments_matching_this_selection')}
          </p>
          <button className="text-sm text-blue-600 hover:underline mt-2" data-id="element-918">
            {t('components.BlockedCardPayments.clear_filters_or_select_another_date')}
          </button>
        </div>
      </div>
    </div>;
}
// Data Generation Functions
function generateChartData(dateRange: DateRange, metric: KeyMetric) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  for (let i = 0; i < dataPoints; i++) {
    // Current period dates
    const currentDate = new Date(dateRange.start);
    currentDate.setDate(currentDate.getDate() + Math.floor(i / dataPoints * days));
    // Previous period dates - actual historical dates (current date - days in range)
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - days);
    let value;
    if (metric === 'success-rate') {
      // Success rate should be between 95-100%
      value = 95 + Math.random() * 5;
    } else if (metric === 'accepted-volume') {
      value = 400000 + Math.random() * 200000;
    } else {
      value = 3500 + Math.floor(Math.random() * 1500);
    }
    current.push({
      date: currentDate.toISOString().split('T')[0],
      rate: metric === 'success-rate' ? parseFloat(value.toFixed(2)) : undefined,
      amount: metric === 'accepted-volume' ? parseFloat(value.toFixed(2)) : undefined,
      count: metric === 'accepted-payments' ? Math.floor(value) : undefined
    });
    // Previous period uses ACTUAL historical date
    previous.push({
      date: previousDate.toISOString().split('T')[0],
      rate: metric === 'success-rate' ? parseFloat((85 + Math.random() * 15).toFixed(2)) : undefined,
      amount: metric === 'accepted-volume' ? parseFloat((value * (0.9 + Math.random() * 0.2)).toFixed(2)) : undefined,
      count: metric === 'accepted-payments' ? Math.floor(value * (0.9 + Math.random() * 0.2)) : undefined
    });
  }
  return {
    current,
    previous
  };
}
function generateCardBreakdownData(dateRange: DateRange, view: BreakdownView, pivotBy: PivotOption) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  const pivotCategories = PIVOT_CONFIG[pivotBy];
  for (let i = 0; i < dataPoints; i++) {
    const date = new Date(dateRange.start);
    date.setDate(date.getDate() + Math.floor(i / dataPoints * days));
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    if (view === 'payment-volume' || view === 'payments') {
      const point: any = {
        date: dateStr
      };
      const totalValue = view === 'payment-volume' ? 300 + Math.random() * 450 : 7 + Math.floor(Math.random() * 9);
      pivotCategories.forEach(cat => {
        const val = totalValue * cat.volumeShare * (0.9 + Math.random() * 0.2);
        point[cat.key] = view === 'payment-volume' ? parseFloat(val.toFixed(2)) : Math.round(val);
      });
      current.push(point);
    } else if (view === 'share-of-payments') {
      const point: any = {
        date: dateStr
      };
      // Generate slightly randomized shares that sum to 100
      let remainingShare = 100;
      pivotCategories.forEach((cat, idx) => {
        if (idx === pivotCategories.length - 1) {
          point[cat.key] = parseFloat(remainingShare.toFixed(2));
        } else {
          const share = cat.volumeShare * 100 * (0.9 + Math.random() * 0.2);
          point[cat.key] = parseFloat(share.toFixed(2));
          remainingShare -= point[cat.key];
        }
      });
      current.push(point);
    } else if (view === 'authorization-rate') {
      const point: any = {
        date: dateStr
      };
      pivotCategories.forEach(cat => {
        // Generate rate around each category's successRateBase with small variance
        const variance = (Math.random() - 0.5) * 4;
        point[cat.key] = parseFloat(Math.min(100, Math.max(0, cat.successRateBase + variance)).toFixed(2));
      });
      current.push(point);
    }
  }
  return {
    current,
    previous
  };
}
function generateFailedPaymentsData(dateRange: DateRange, view: FailedView) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  // Category shares: insufficient funds 35%, incorrect card 20%, issuer declined 18%, fraud 12%, expired 10%, other 5%
  const shares = {
    insufficientFunds: 0.35,
    incorrectCardDetails: 0.2,
    issuerDeclined: 0.18,
    suspectedFraud: 0.12,
    expiredCard: 0.1,
    other: 0.05
  };
  for (let i = 0; i < dataPoints; i++) {
    const date = new Date(dateRange.start);
    date.setDate(date.getDate() + Math.floor(i / dataPoints * days));
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    if (view === 'failed-volume') {
      const totalFailed = 20000 + Math.random() * 10000;
      current.push({
        date: dateStr,
        insufficientFunds: parseFloat((totalFailed * shares.insufficientFunds * (0.9 + Math.random() * 0.2)).toFixed(2)),
        incorrectCardDetails: parseFloat((totalFailed * shares.incorrectCardDetails * (0.9 + Math.random() * 0.2)).toFixed(2)),
        issuerDeclined: parseFloat((totalFailed * shares.issuerDeclined * (0.9 + Math.random() * 0.2)).toFixed(2)),
        suspectedFraud: parseFloat((totalFailed * shares.suspectedFraud * (0.9 + Math.random() * 0.2)).toFixed(2)),
        expiredCard: parseFloat((totalFailed * shares.expiredCard * (0.9 + Math.random() * 0.2)).toFixed(2)),
        other: parseFloat((totalFailed * shares.other * (0.9 + Math.random() * 0.2)).toFixed(2))
      });
    } else if (view === 'failed') {
      const totalCount = 60 + Math.floor(Math.random() * 40);
      current.push({
        date: dateStr,
        insufficientFunds: Math.round(totalCount * shares.insufficientFunds * (0.9 + Math.random() * 0.2)),
        incorrectCardDetails: Math.round(totalCount * shares.incorrectCardDetails * (0.9 + Math.random() * 0.2)),
        issuerDeclined: Math.round(totalCount * shares.issuerDeclined * (0.9 + Math.random() * 0.2)),
        suspectedFraud: Math.round(totalCount * shares.suspectedFraud * (0.9 + Math.random() * 0.2)),
        expiredCard: Math.round(totalCount * shares.expiredCard * (0.9 + Math.random() * 0.2)),
        other: Math.round(totalCount * shares.other * (0.9 + Math.random() * 0.2))
      });
    } else if (view === 'share-of-failures') {
      // Shares vary slightly per period but always sum to ~100
      const variance = () => 0.9 + Math.random() * 0.2;
      const raw = {
        insufficientFunds: shares.insufficientFunds * variance(),
        incorrectCardDetails: shares.incorrectCardDetails * variance(),
        issuerDeclined: shares.issuerDeclined * variance(),
        suspectedFraud: shares.suspectedFraud * variance(),
        expiredCard: shares.expiredCard * variance(),
        other: shares.other * variance()
      };
      const sum = Object.values(raw).reduce((a, b) => a + b, 0);
      current.push({
        date: dateStr,
        insufficientFunds: parseFloat((raw.insufficientFunds / sum * 100).toFixed(2)),
        incorrectCardDetails: parseFloat((raw.incorrectCardDetails / sum * 100).toFixed(2)),
        issuerDeclined: parseFloat((raw.issuerDeclined / sum * 100).toFixed(2)),
        suspectedFraud: parseFloat((raw.suspectedFraud / sum * 100).toFixed(2)),
        expiredCard: parseFloat((raw.expiredCard / sum * 100).toFixed(2)),
        other: parseFloat((raw.other / sum * 100).toFixed(2))
      });
    } else if (view === 'failure-rate') {
      const previousDate = new Date(date);
      previousDate.setDate(previousDate.getDate() - days);
      current.push({
        date: date.toISOString().split('T')[0],
        value: parseFloat((1.5 + Math.random() * 1.5).toFixed(2))
      });
      previous.push({
        date: previousDate.toISOString().split('T')[0],
        value: parseFloat((1.5 + Math.random() * 1.5).toFixed(2))
      });
    }
  }
  return {
    current,
    previous
  };
}